import Msg from "./message";
import Field from "./field";
import EmailSettings from "./email-settings";
import Api from "./api";
import { getEntries, getEntryParams, replaceAll, uniq } from "../utils";
import ISubstitution from "../interface/substitution";
import IRelationPathField from "../interface/relation-path-field";
import IEmailSignature from "../interface/email-signature";
import IEmailCustomTag from "../interface/email-custom-tag";
import IApiField from "../interface/api/api-field";
import SubstitutionOption from "../enum/substitution-option";
import ServiceUtil from "../enum/service-util";
import Message from "../enum/message";
import { ValuableTypes } from "../enum/field-types";
import FieldType from "../enum/field-type";

class ServiceUtilsSingleton {

    /* Public Methods - Common */

    public validPath(pathId: number[]): boolean {
        for (let i = 0; i < pathId.length; i++) {
            const field: IApiField | null = Api.getField(pathId[i]);
            if (!field) {
                return false;
            }
            if (i < pathId.length - 1 && field.uiType !== FieldType.pointer) {
                return false;
            }
        }
        return true;
    }

    /* Public Methods - Substitutions */

    public getPathSubstitutions(entityId: number): ISubstitution[] {
        return this.substitutions(entityId, ValuableTypes);
    }

    public getSourceSubstitutions(entityId: number, source: string): ISubstitution[] {
        const subs: ISubstitution[] = this.getPathSubstitutions(entityId);
        const result: ISubstitution[] = [];
        subs.forEach((sub: ISubstitution) => {
            const subId: string = `[${ServiceUtil.Sub}](${sub.pathId};;${sub.option})`;
            if (source.indexOf(subId) > -1) {
                result.push(sub);
            }
        });
        return result;
    }

    public replaceSubsToName(entityId: number, source: string): string {
        const subs: ISubstitution[] = this.getPathSubstitutions(entityId);
        return this.replaceEntries(source, ServiceUtil.Sub, (params: (string | number)[]) => {
            const path: string = params[0].toString();
            const option: SubstitutionOption = params[1] as SubstitutionOption;
            const sub: ISubstitution | undefined = subs.find((s: ISubstitution) => {
                return `${s.pathId}` === path && s.option === option;
            });
            if (sub) {
                return `@${sub.name}`;
            }
        });
    }

    public replaceSubsToId(entityId: number, source: string): string {
        let result = `${source}`.trim();
        const subs: ISubstitution[] = this.getPathSubstitutions(entityId).sort((a: ISubstitution, b: ISubstitution) => a.name.length > b.name.length ? -1 : 1);
        subs.forEach((sub: ISubstitution) => {
            const systemId: string = `[${ServiceUtil.Sub}](${sub.pathId};;${sub.option})`;
            result = replaceAll(result, `@${sub.name}`, systemId);
            result = replaceAll(result, encodeURI(`@${sub.name}`), systemId);
        });
        return result;
    }

    /* Public Methods - Custom Tags */

    public replaceTagsToId(body: string): string {
        const tags: IEmailCustomTag[] = EmailSettings.tagGetAll();
        let result = `${body}`.trim();
        tags.forEach((tag: IEmailCustomTag) => {
            result = replaceAll(result, `<${tag.name}>`, `[${ServiceUtil.TagStart}](${tag.id})`);
            result = replaceAll(result, `</${tag.name}>`, `[${ServiceUtil.TagEnd}](${tag.id})`);
        });
        return result;
    }

    public replaceTagsToName(body: string): string {
        let result = `${body}`.trim();
        const tags: IEmailCustomTag[] = EmailSettings.tagGetAll();
        result = this.replaceEntries(result, ServiceUtil.TagStart, (params: (string | number)[]) => {
            const tag: IEmailCustomTag | undefined = tags.find((tag: IEmailCustomTag) => tag.id === params[0]);
            if (tag) {
                return `<${tag.name}>`;
            }
        });
        result = this.replaceEntries(result, ServiceUtil.TagEnd, (params: (string | number)[]) => {
            const tag: IEmailCustomTag | undefined = tags.find((tag: IEmailCustomTag) => tag.id === params[0]);
            if (tag) {
                return `</${tag.name}>`;
            }
        });
        return result;
    }

    /* Public Methods - Signatures */

    public replaceSignsToId(original: string): string {
        const signs: IEmailSignature[] = EmailSettings.signGetAll();
        return this.replaceEntries(original, ServiceUtil.Sign, (params: (string | number)[]) => {
            const name: string = params[0] as string;
            const sign: IEmailSignature | undefined = signs.find((s: IEmailSignature) => s.name === name);
            if (sign) {
                return `[${ServiceUtil.Sign}](${sign.id})`;
            }
        });
    }

    /* Public Methods - Common */

    public getEntries(original: string, entryName: string): string[] {
        let result: string[] = [];
        let str: string = `${original}`;
        const regexParam: RegExp = new RegExp(`\\[${entryName}\\]\\(((.|[\r\n])*?)\\)`, 'g');
        const regexClear: RegExp = new RegExp(`\\[${entryName}\\]`, 'g');
        result = result.concat(uniq(getEntries(str, regexParam)));
        result.forEach((entry: string) => {
            str = replaceAll(str, entry, '');
        });
        result = result.concat(uniq(getEntries(str, regexClear)));
        return result;
    }

    public replaceEntries(original: string, entryName: string, callback: Function): string {
        let result = `${original}`.trim();
        const entries: string[] = this.getEntries(result, entryName);
        entries.forEach((entry: string) => {
            const params: (string | number)[] = getEntryParams(entry);
            const replace: string = callback(params) || '';
            result = replaceAll(result, entry, replace);
        });
        return result;
    }

    public substitutions(entityId: number, fieldTypes: FieldType[], level: number = 2, includeOptions: boolean = true): ISubstitution[] {
        const result: ISubstitution[] = [];
        const subs: IRelationPathField[] = Field.getRelationPathsFields(entityId, fieldTypes, level);
        subs.forEach((sub: IRelationPathField) => {
            result.push({
                id: sub.pathId.join(','),
                pathId: sub.pathId,
                name: sub.pathName,
                option: SubstitutionOption.None,
                type: sub.type
            });
            if (includeOptions && sub.type === FieldType.datetime) {
                result.push({
                    id: `${sub.pathId.join(',')}-${SubstitutionOption.DateOnly}`,
                    pathId: sub.pathId,
                    name: `${sub.pathName} - ${Msg.get(Message.dateonly)}`,
                    option: SubstitutionOption.DateOnly,
                    type: sub.type
                });
                result.push({
                    id: `${sub.pathId.join(',')}-${SubstitutionOption.TimeOnly}`,
                    pathId: sub.pathId,
                    name: `${sub.pathName} - ${Msg.get(Message.timeonly)}`,
                    option: SubstitutionOption.TimeOnly,
                    type: sub.type
                });
            }
        });
        return result;
    }

}

const ServiceUtils = new ServiceUtilsSingleton();

export default ServiceUtils;